import { codeFont, numFont } from '../../../utils/fonts';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import HelpOutlineOutlined from '@mui/icons-material/HelpOutlineOutlined';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import SwapHorizOutlined from '@mui/icons-material/SwapHorizOutlined';
import { formatPriceWithCurrency } from '../../../utils/vat';

const STATUS_CONFIG = {
  ok: {
    color: '#2f8c54',
    bg: '#fff',
    borderColor: 'divider',
    Icon: CheckCircleOutlined,
  },
  ambiguous: {
    color: '#b45309',
    bg: '#fef3e2',
    borderColor: 'rgba(180, 83, 9, 0.2)',
    Icon: HelpOutlineOutlined,
  },
  missing: {
    color: '#dc2626',
    bg: '#fee2e2',
    borderColor: 'rgba(220, 38, 38, 0.2)',
    Icon: ErrorOutlineOutlined,
  },
};

export default function PartStatusRow({ part, onSwap, selectedVariant }) {
  const { t } = useTranslation();
  const config = STATUS_CONFIG[part.status] || STATUS_CONFIG.missing;
  const StatusIcon = config.Icon;

  const price =
    part.matched_part_price ||
    part.auto_picked?.sales_price ||
    part.auto_picked?.purchase_price ||
    part.catalogue_candidate?.purchase_price;
  const variants = part.ezyvin_variants || [];
  // Ezyvin repeats codes across its raw list — dedupe by (code, brand) and sort by
  // brand so the alternatives read as a tidy brand list, not 47 near-identical rows.
  const nonSyntheticVariants = React.useMemo(() => {
    const seen = new Set();
    const unique = [];
    for (const v of variants) {
      if (v.is_synthetic) continue;
      const key = `${v.part_number}|${v.supplier_name}`;
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(v);
    }
    unique.sort((a, b) => (a.supplier_name || '').localeCompare(b.supplier_name || ''));
    return unique;
  }, [variants]);
  const hasVariantChoices = nonSyntheticVariants.length > 1;
  const picked = selectedVariant || (nonSyntheticVariants.length === 1 ? nonSyntheticVariants[0] : null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: '12px 16px',
          bgcolor: config.bg,
          borderRadius: '10px',
          border: 1,
          borderColor: config.borderColor,
        }}
      >
        <StatusIcon sx={{ fontSize: 18, color: config.color, flexShrink: 0 }} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{ fontSize: 13, fontWeight: 500, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {part.matched_part_name || part.display_name || part.ezyvin_part_name}
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', ...codeFont, mt: 0.25 }}>
            {picked ? (
              <>
                {picked.part_number}{picked.supplier_name ? ` · ${picked.supplier_name}` : ''}
                {picked.oe_number ? ` · OE: ${picked.oe_number}` : ''}
              </>
            ) : part.match_source === 'catalogue' && part.catalogue_candidate?.source_code ? (
              // The orderable catalogue code, not Ezyvin's reference number
              part.catalogue_candidate.source_code
            ) : part.ezyvin_part_number ? (
              part.ezyvin_part_number
            ) : (
              <Typography component="span" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500, color: config.color, fontSize: 'inherit' }}>
                {part.status === 'missing' ? t('oem.willBeCreated') : ''}
              </Typography>
            )}
            {part.match_source === 'catalogue' ? (
              <Typography component="span" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500, color: '#2f8c54', fontSize: 'inherit' }}>
                {' · '}{t('oem.fromVendorCatalogue')}
                {(part.catalogue_candidate?.vendor_name || part.catalogue_candidate?.catalogue_name)
                  ? `: ${part.catalogue_candidate.vendor_name || part.catalogue_candidate.catalogue_name}`
                  : ''}
                {part.catalogue_candidate?.brand ? ` · ${part.catalogue_candidate.brand}` : ''}
              </Typography>
            ) : part.match_source === 'supplier' && part.auto_picked?.supplier_name ? (
              <Typography component="span" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500, color: '#2f8c54', fontSize: 'inherit' }}>
                {' · '}{t('oem.fromSupplier')}: {part.auto_picked.supplier_name}
              </Typography>
            ) : part.match_source?.startsWith('catalog') && (
              <Typography component="span" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500, color: '#2f8c54', fontSize: 'inherit' }}>
                {' · '}{t('oem.fromCatalog')}
              </Typography>
            )}
          </Typography>
        </Box>

        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {part.status === 'ok' && part.price_missing ? (
            // Matched in the own register but the part has no usable price — never
            // present a satisfied-looking green 0,00 € line.
            <>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#b45309' }}>
                {t('oem.priceMissing')}
              </Typography>
              {onSwap && (
                <Button size="small" onClick={() => onSwap(part)} sx={{ minWidth: 'auto', p: 0.5, color: 'text.secondary' }}>
                  <SwapHorizOutlined sx={{ fontSize: 16 }} />
                </Button>
              )}
            </>
          ) : part.status === 'ok' && (price || part.match_source === 'catalogue') ? (
            // A catalogue match without a known purchase price still gets the swap
            // button — never a dead row contradicting its own "Tuoteluettelosta" chip.
            <>
              {price ? (
                <Typography sx={{ ...numFont, fontSize: 13, fontWeight: 600 }}>
                  {formatPriceWithCurrency(price)}
                </Typography>
              ) : null}
              {onSwap && (
                <Button size="small" onClick={() => onSwap(part)} sx={{ minWidth: 'auto', p: 0.5, color: 'text.secondary' }}>
                  <SwapHorizOutlined sx={{ fontSize: 16 }} />
                </Button>
              )}
            </>
          ) : onSwap && (hasVariantChoices || part.status !== 'ok') ? (
            // Not matched anywhere — one entry point to the unified part-source picker
            <Button
              size="small"
              onClick={() => onSwap(part)}
              sx={{ fontSize: 12, borderRadius: '8px', py: 0.25, px: 1.5, textTransform: 'none', color: '#b45309' }}
            >
              {t('oem.choosePart')}
              {nonSyntheticVariants.length > 1 ? ` (${nonSyntheticVariants.length})` : ''}
            </Button>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
